import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { Navigate } from "react-router-dom";
import { useUserContext } from "../Context/UserContext";
import RequireAuthenticatedRoute from "../Login/RequireAuthenticatedRoute";
import { reviewApi } from "../Reviews/api";

export type AccountType = "customer" | "attorney" | "admin";
export type PortalAccess = {
  accountType: AccountType;
  admin: boolean;
  attorney: boolean;
  profile: { status: "pending" | "approved" | "declined" | "suspended" } | null;
  paymentsEnabled?: boolean;
  targetedBeta?: boolean;
  feeMode?: "test" | "live";
  /** The charge authorization a client agrees to when submitting a paid review. */
  chargeAuthorization?: string;
};
export function portalHome(access: PortalAccess) {
  if (access.accountType === "admin") return "/admin/reviews";
  if (access.accountType === "attorney")
    return access.attorney ? "/attorney/reviews" : "/attorney/profile";
  return "/questions/new";
}
const PortalContext = createContext<{
  access: PortalAccess | null;
  error: string;
  refresh: () => void;
}>({ access: null, error: "", refresh: () => {} });
export const usePortal = () => useContext(PortalContext);

export function PortalProvider({ children }: { children: React.ReactNode }) {
  const { user } = useUserContext();
  const userId = user?.id;
  const [state, setState] = useState<{
    userId: typeof userId;
    access: PortalAccess | null;
    error: string;
  }>({ userId: undefined, access: null, error: "" });
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision(v => v + 1), []);
  useEffect(() => {
    if (!userId) {
      setState({ userId: undefined, access: null, error: "" });
      return;
    }
    const c = new AbortController();
    reviewApi<PortalAccess>("/me", "GET", undefined, c.signal)
      .then(access => {
        if (c.signal.aborted) return;
        if (!["customer", "attorney", "admin"].includes(access.accountType))
          throw new Error(
            "Could not determine your account portal. Please retry."
          );
        setState({ userId, access, error: "" });
      })
      .catch(e => {
        if (!c.signal.aborted)
          setState(previous => ({
            userId,
            access: previous.userId === userId ? previous.access : null,
            error: e.message || "Could not load your account portal.",
          }));
      });
    return () => c.abort();
  }, [userId, revision]);
  useEffect(() => {
    window.addEventListener("focus", refresh);
    window.addEventListener("review-access-changed", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener("review-access-changed", refresh);
    };
  }, [refresh]);
  // Never expose a previous user's portal while a new session is loading.
  const current =
    userId && state.userId === userId ? state : { access: null, error: "" };
  return (
    <PortalContext.Provider
      value={{ access: current.access, error: current.error, refresh }}
    >
      {children}
    </PortalContext.Provider>
  );
}

function PortalContent({
  children,
  allowed,
  approvedAttorney,
  review,
  home,
}: {
  children?: React.ReactNode;
  allowed?: AccountType[];
  approvedAttorney?: boolean;
  review?: boolean;
  home?: boolean;
}) {
  const { access, error, refresh } = usePortal();
  if (!access)
    return (
      <main className="reviewed-documents">
        {error ? (
          <>
            <p role="alert">{error}</p>
            <button className="btn btn-primary" onClick={refresh}>
              Retry
            </button>
          </>
        ) : (
          <p role="status">Opening your portal…</p>
        )}
      </main>
    );
  if (
    home ||
    (allowed && !allowed.includes(access.accountType)) ||
    (approvedAttorney && !access.attorney) ||
    (review && access.accountType === "attorney" && !access.attorney)
  )
    return <Navigate replace to={portalHome(access)} />;
  return <>{children}</>;
}

export function PortalRoute(props: React.ComponentProps<typeof PortalContent>) {
  return (
    <RequireAuthenticatedRoute>
      <PortalContent {...props} />
    </RequireAuthenticatedRoute>
  );
}
